/**
 * CommunicationsHub — the "Communications" top nav for the Athletic Director
 * and Scheduler (AJ, 2026-10-12). One page, four tabs, every tab reusing the
 * existing screen so nothing drifts from the Tech Specialist versions:
 *   • News             — WebsiteContentPanel kind="posts" (was "Post News")
 *   • Welcome Letters  — Draft Board's WelcomeTab + a registration picker
 *   • Birthday Reminders — BirthdayRemindersPanel (Family Directory birthdays)
 *   • Messaging        — MessagingPanel (was the AD "Messaging" tab /
 *                        Scheduler sport sub-tab)
 *   • Program Schedule — ProgramSchedulePanel (monthly per-sport task email)
 * See docs/COMMUNICATIONS_HUB.md.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Newspaper, Mail, Cake, MessageSquare, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import WebsiteContentPanel from './WebsiteContentPanel';
import ClubChatPanel from './ClubChatPanel';
import { WelcomeTab } from './DraftBoardPanel';
import BirthdayRemindersPanel from './BirthdayRemindersPanel';
import ProgramSchedulePanel from './ProgramSchedulePanel';

const FORMS_API = `${process.env.REACT_APP_BACKEND_URL}/api/reg-builder`;

const TABS = [
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'welcome', label: 'Welcome Letters', icon: Mail },
  { key: 'birthdays', label: 'Birthday Reminders', icon: Cake },
  { key: 'messaging', label: 'Messaging', icon: MessageSquare },
  { key: 'program', label: 'Program Schedule', icon: CalendarClock },
];

function WelcomeLetters({ orgId }) {
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState('');
  useEffect(() => {
    if (!orgId) return;
    axios.get(`${FORMS_API}/${orgId}/forms`)
      .then((r) => { const f = r.data.forms || []; setForms(f); if (f.length) setFormId((cur) => cur || f[0].id); })
      .catch(() => setForms([]));
  }, [orgId]);
  return (
    <div className="space-y-3">
      <select className="h-8 text-sm rounded px-2 border border-slate-300 bg-white text-slate-900"
        value={formId} onChange={(e) => setFormId(e.target.value)} data-testid="comm-welcome-form-select">
        <option value="">Pick a registration…</option>
        {forms.map((f) => <option key={f.id} value={f.id}>{f.name}{f.season ? ` — ${f.season}` : ''}</option>)}
      </select>
      {formId
        ? <WelcomeTab orgId={orgId} formId={formId} published={formId} />
        : <p className="text-sm text-slate-500">Pick a registration to manage its welcome letters.</p>}
    </div>
  );
}

export default function CommunicationsHub({ orgId, user, userRole, teams }) {
  const [tab, setTab] = useState('news');
  return (
    <div className="space-y-4" data-testid="communications-hub">
      <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {TABS.map((t) => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <Button key={t.key} variant="ghost" size="sm" onClick={() => setTab(t.key)}
              className={`h-8 px-3 text-xs font-medium border ${active ? 'bg-teal-50 text-teal-700 border-teal-200' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              data-testid={`comm-tab-${t.key}`}>
              <Icon className="h-3.5 w-3.5 mr-1.5" />{t.label}
            </Button>
          );
        })}
      </div>

      {tab === 'news' && <WebsiteContentPanel orgId={orgId} kind="posts" />}
      {tab === 'welcome' && <WelcomeLetters orgId={orgId} />}
      {tab === 'birthdays' && <BirthdayRemindersPanel orgId={orgId} />}
      {tab === 'program' && <ProgramSchedulePanel orgId={orgId} />}
      {/* Messaging = the club app's chats (AD + Scheduler, AJ 2026-10-16; docs/OL_ACCESS_LINKS.md §10/§16). */}
      {tab === 'messaging' && <ClubChatPanel orgId={orgId} />}
    </div>
  );
}
